import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trash2, Plus, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/* ── Sortable Column Card (DnD-reorderable table column) ── */
function SortableColumnCard({ id, col, index, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-2 border rounded-md bg-gray-50 space-y-1"
      data-testid={`column-card-${index}`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1">
          <button
            className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-200"
            {...attributes}
            {...listeners}
            data-testid={`column-drag-handle-${index}`}
          >
            <GripVertical className="w-3 h-3 text-gray-400" />
          </button>
          <span className="text-[10px] text-gray-400">Col {index + 1}</span>
        </div>
        <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => onRemove(index)}>
          <Trash2 className="w-2.5 h-2.5" />
        </Button>
      </div>
      <Input value={col.header} onChange={(e) => onUpdate(index, "header", e.target.value)} placeholder="Header" className="h-6 text-xs" />
      <Input value={col.key}    onChange={(e) => onUpdate(index, "key", e.target.value)}    placeholder="Data key (e.g. description)" className="h-6 text-xs font-mono" />
      <div className="flex gap-1">
        <Input value={col.width} onChange={(e) => onUpdate(index, "width", e.target.value)} placeholder="Width" className="h-6 text-xs flex-1" />
        <Select value={col.align} onValueChange={(v) => onUpdate(index, "align", v)}>
          <SelectTrigger className="h-6 w-16 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">L</SelectItem>
            <SelectItem value="center">C</SelectItem>
            <SelectItem value="right">R</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-[9px] text-gray-400">Show Total</Label>
        <Switch checked={col.showTotal || false} onCheckedChange={(v) => onUpdate(index, "showTotal", v)} className="scale-75" />
      </div>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[9px] text-gray-400">Vertical Borders</Label>
        <div className="flex gap-0.5">
          <button
            type="button"
            data-testid={`col-${index}-border-left`}
            onClick={() => onUpdate(index, "borderLeft", !col.borderLeft)}
            title="Left border"
            className={`h-5 w-7 text-[9px] rounded border transition ${col.borderLeft ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-500 border-gray-200"}`}
          >L</button>
          <button
            type="button"
            data-testid={`col-${index}-border-right`}
            onClick={() => onUpdate(index, "borderRight", !col.borderRight)}
            title="Right border"
            className={`h-5 w-7 text-[9px] rounded border transition ${col.borderRight ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-500 border-gray-200"}`}
          >R</button>
        </div>
      </div>
    </div>
  );
}

/* ── Columns Editor (DnD reorderable list of columns for lineItems / summaryTable) ── */
export function ColumnsEditor({ columns, onChange }) {
  const colSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }));
  const columnIds = columns.map((_, i) => `col-${i}`);

  const handleColDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = columnIds.indexOf(String(active.id));
    const newIdx = columnIds.indexOf(String(over.id));
    if (oldIdx >= 0 && newIdx >= 0) onChange(arrayMove(columns, oldIdx, newIdx));
  };

  const add = () =>
    onChange([
      ...columns,
      { header: "Column", key: "field", width: "20%", align: "left", showTotal: false },
    ]);
  const remove = (i) => onChange(columns.filter((_, idx) => idx !== i));
  const update = (i, key, val) =>
    onChange(columns.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));

  return (
    <div className="space-y-2">
      <DndContext sensors={colSensors} collisionDetection={closestCenter} onDragEnd={handleColDragEnd}>
        <SortableContext items={columnIds} strategy={verticalListSortingStrategy}>
          {columns.map((col, i) => (
            <SortableColumnCard key={`col-${i}`} id={`col-${i}`} col={col} index={i} onUpdate={update} onRemove={remove} />
          ))}
        </SortableContext>
      </DndContext>
      <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={add} data-testid="add-column-btn">
        <Plus className="w-3 h-3 mr-1" /> Add Column
      </Button>
    </div>
  );
}

/* ── Column Groups Editor (nested header support) ── */
export function ColumnGroupsEditor({ groups, columns, onChange }) {
  const add = () => onChange([...groups, { label: "Group", startCol: 0, span: 2 }]);
  const remove = (i) => onChange(groups.filter((_, idx) => idx !== i));
  const update = (i, key, val) => onChange(groups.map((g, idx) => idx === i ? { ...g, [key]: val } : g));

  return (
    <div className="space-y-2">
      {groups.map((g, i) => (
        <div key={i} className="p-2 border rounded-md bg-gray-50 space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400">Group {i + 1}</span>
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => remove(i)}>
              <Trash2 className="w-2.5 h-2.5" />
            </Button>
          </div>
          <Input value={g.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Group Header" className="h-6 text-xs" />
          <div className="flex gap-1">
            <div className="flex-1">
              <Label className="text-[9px] text-gray-400">Start Col</Label>
              <Select value={String(g.startCol)} onValueChange={(v) => update(i, "startCol", parseInt(v))}>
                <SelectTrigger className="h-6 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(columns || []).map((_, ci) => <SelectItem key={ci} value={String(ci)} className="text-xs">Col {ci + 1}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-[9px] text-gray-400">Span</Label>
              <Select value={String(g.span)} onValueChange={(v) => update(i, "span", parseInt(v))}>
                <SelectTrigger className="h-6 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: Math.max(1, (columns || []).length - (g.startCol || 0)) }, (_, j) => j + 1).map(n => (
                    <SelectItem key={n} value={String(n)} className="text-xs">{n} col{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={add}>
        <Plus className="w-3 h-3 mr-1" /> Add Group Header
      </Button>
    </div>
  );
}

/* ── Sub-Rows Editor (extra rows below each line item) ── */
export function SubRowsEditor({ subRows, columns, onChange }) {
  const add = () =>
    onChange([
      ...subRows,
      {
        key: "description",
        label: "",
        startCol: 1,
        colSpan: Math.max(1, (columns || []).length - 1),
        fontSize: 10,
        color: "#6B7280",
        fontStyle: "italic",
        align: "left",
      },
    ]);
  const remove = (i) => onChange(subRows.filter((_, idx) => idx !== i));
  const update = (i, key, val) =>
    onChange(subRows.map((sr, idx) => (idx === i ? { ...sr, [key]: val } : sr)));

  const totalCols = (columns || []).length;

  return (
    <div className="space-y-2">
      {subRows.map((sr, i) => (
        <div key={i} className="p-2 border rounded-md bg-gray-50 space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400">Sub-row {i + 1}</span>
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => remove(i)}>
              <Trash2 className="w-2.5 h-2.5" />
            </Button>
          </div>
          <Input value={sr.key}        onChange={(e) => update(i, "key", e.target.value)}   placeholder="Data key (e.g. description)" className="h-6 text-xs font-mono" />
          <Input value={sr.label || ""} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label prefix (optional)"      className="h-6 text-xs" />
          <div className="flex gap-1">
            <div className="flex-1">
              <Label className="text-[9px] text-gray-400">Start Col</Label>
              <Select value={String(sr.startCol || 0)} onValueChange={(v) => update(i, "startCol", parseInt(v))}>
                <SelectTrigger className="h-6 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(columns || []).map((col, ci) => (
                    <SelectItem key={ci} value={String(ci)} className="text-xs">{ci + 1}: {col.header}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-[9px] text-gray-400">Span</Label>
              <Select value={String(sr.colSpan || 1)} onValueChange={(v) => update(i, "colSpan", parseInt(v))}>
                <SelectTrigger className="h-6 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: totalCols - (sr.startCol || 0) }, (_, j) => j + 1).map((n) => (
                    <SelectItem key={n} value={String(n)} className="text-xs">{n} col{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-1">
            <Input
              type="number"
              value={sr.fontSize || 10}
              onChange={(e) => update(i, "fontSize", Number(e.target.value))}
              className="h-6 text-xs flex-1"
              min={6}
              max={20}
              placeholder="Size"
            />
            <input
              type="color"
              value={sr.color || "#6B7280"}
              onChange={(e) => update(i, "color", e.target.value)}
              className="w-6 h-6 rounded border border-gray-200 cursor-pointer p-0.5"
            />
            <Select value={sr.fontStyle || "italic"} onValueChange={(v) => update(i, "fontStyle", v)}>
              <SelectTrigger className="h-6 w-16 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Abc</SelectItem>
                <SelectItem value="italic">_Abc_</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sr.align || "left"} onValueChange={(v) => update(i, "align", v)}>
              <SelectTrigger className="h-6 w-12 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">L</SelectItem>
                <SelectItem value="center">C</SelectItem>
                <SelectItem value="right">R</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={add} data-testid="add-subrow-btn">
        <Plus className="w-3 h-3 mr-1" /> Add Sub-Row
      </Button>
    </div>
  );
}

/* ── Totals Rows Editor (for the totals element) ── */
export function TotalsRowsEditor({ rows, onChange }) {
  const add = () =>
    onChange([
      ...rows,
      { label: "Label", ejsBinding: "", fontWeight: "normal" },
    ]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const update = (i, key, val) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="p-2 border rounded-md bg-gray-50 space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400">Row {i + 1}</span>
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => remove(i)}>
              <Trash2 className="w-2.5 h-2.5" />
            </Button>
          </div>
          <Input value={r.label}          onChange={(e) => update(i, "label", e.target.value)}      placeholder="Label" className="h-6 text-xs" />
          <Input value={r.ejsBinding || ""} onChange={(e) => update(i, "ejsBinding", e.target.value)} placeholder="EJS binding" className="h-6 text-xs font-mono" />
          <Select value={r.fontWeight} onValueChange={(v) => update(i, "fontWeight", v)}>
            <SelectTrigger className="h-6 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ))}
      <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={add}>
        <Plus className="w-3 h-3 mr-1" /> Add Row
      </Button>
    </div>
  );
}
